import { gearboxMarketConfigurators } from '@1delta/data-sdk'
import { zeroAddress } from 'viem'
import { toGenericPriceKey, toOracleKey } from '../../assets'
import { parseRawAmount } from '../../utils/parsing'
import { GenericTokenList } from '../../types'
import { composeVaultDisplayName } from '../displayName'
import { deriveConvertToAssets } from '../sharePrice'
import { GearboxV3Pool, GearboxV3Pools } from './types'

// Gearbox uses WETH on-chain. Normalize to `zeroAddress` on Ethereum mainnet
// to match the rest of the stack's native-ETH convention. Other chains may
// use different wrapped-native addresses; we leave those untouched.
const WETH_MAINNET = '0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2'
const normalizeUnderlying = (addr: string, chainId: string): string => {
  const lower = addr.toLowerCase()
  if (chainId === '1' && lower === WETH_MAINNET) return zeroAddress
  return lower
}

/** Gearbox rates are in RAY (1e27). Divide by 1e25 to get percent APR. */
const RAY_TO_PERCENT = 1e25
const scaleRate = (raw: string | number | bigint | undefined): number => {
  if (raw === undefined || raw === null) return 0
  return Number(raw) / RAY_TO_PERCENT
}

/**
 * Parser for the single-call Gearbox V3.1 pool fetch built by
 * `buildGearboxV3PoolsCall`.
 *
 *   data[0] = MarketData[]   (from MarketCompressor.getMarkets)
 *
 * Each `MarketData.pool` carries the full ERC-4626 + rate snapshot we
 * need; we skip `creditManagers[]`, `quotaKeeper`, `priceOracle`, and
 * `lossPolicy` here — those belong to the credit-side fetcher under
 * `lending/public-data/gearbox/`. Returns a map keyed by lowercased
 * underlying address (or `zeroAddress` for mainnet WETH, per the
 * native-ETH convention).
 *
 * `totalAssets` (old DataCompressor field) maps to `expectedLiquidity`
 * in V3.1 — Gearbox PoolV3's ERC-4626 `totalAssets()` returns
 * `expectedLiquidity`, so they're interchangeable.
 */
export const getGearboxV3PoolsConverter = (
  chainId: string,
  prices: { [asset: string]: number } = {},
  tokenList: GenericTokenList = {},
): [(data: any[]) => GearboxV3Pools | undefined, number] => {
  const expectedNumberOfCalls = 1

  return [
    (data: any[]) => {
      if (data.length !== expectedNumberOfCalls) return undefined
      const [markets] = data
      return parseMarkets(markets ?? [], chainId, prices, tokenList)
    },
    expectedNumberOfCalls,
  ]
}

function parseMarkets(
  markets: any[],
  chainId: string,
  prices: { [asset: string]: number },
  tokenList: GenericTokenList,
): GearboxV3Pools {
  const out: GearboxV3Pools = {}

  // Configurator address → curator display name lookup, populated by the
  // `lender-metadata` registry per chain. Keys in the registry are stored
  // lowercased (matches how we lowercase `m.configurator` below).
  const configuratorNames = gearboxMarketConfigurators(chainId) ?? {}

  for (const m of markets) {
    const pool = m?.pool
    if (!pool) continue
    if (pool.isPaused) continue

    const rawAddress = (pool.baseParams?.addr ?? '').toString().toLowerCase()
    const rawUnderlying = (pool.underlying ?? '').toString().toLowerCase()
    if (!rawAddress || !rawUnderlying) continue
    const underlying = normalizeUnderlying(rawUnderlying, chainId)

    const assetMeta = tokenList[underlying]
    const decimals = Number(assetMeta?.decimals ?? pool.decimals ?? 18)

    // Pool's ERC-4626 `totalAssets()` is `expectedLiquidity`.
    const totalAssetsRaw = pool.expectedLiquidity?.toString() ?? '0'
    const totalSupplyRaw = pool.totalSupply?.toString() ?? '0'
    const totalAssetsFormatted = Number(
      parseRawAmount(totalAssetsRaw, decimals),
    )
    // Diesel shares share the asset's decimals (no offset).
    const convertToAssets = deriveConvertToAssets(
      totalAssetsRaw,
      totalSupplyRaw,
      decimals,
    )

    const oracleKey =
      toOracleKey(assetMeta?.assetGroup) ??
      toGenericPriceKey(underlying, chainId)
    const priceUsd = prices[oracleKey] ?? prices[underlying] ?? 0

    // `availableLiquidity = expectedLiquidity − totalBorrowed` on-chain —
    // the amount a lender can actually pull out right now. Surface it
    // under the cross-vault `liquidity*` names for parity with Morpho /
    // Fluid / Silo.
    const liquidityRaw = pool.availableLiquidity?.toString() ?? '0'
    const liquidityFormatted = Number(parseRawAmount(liquidityRaw, decimals))

    const configurator = (m?.configurator ?? '').toString().toLowerCase()
    const curatorName = configurator
      ? configuratorNames[configurator]
      : undefined

    const entry: GearboxV3Pool = {
      address: rawAddress,
      underlying,
      symbol: pool.symbol ?? '',
      name: pool.name ?? '',
      displayName: composeVaultDisplayName(
        'Gearbox',
        curatorName,
        assetMeta,
        pool.name ?? undefined,
      ),
      decimals,
      totalAssets: totalAssetsRaw,
      totalSupply: totalSupplyRaw,
      convertToAssets,
      totalBorrowed: pool.totalBorrowed?.toString() ?? '0',
      availableLiquidity: pool.availableLiquidity?.toString() ?? '0',
      expectedLiquidity: pool.expectedLiquidity?.toString() ?? '0',
      dieselRate: pool.dieselRate?.toString() ?? '0',
      withdrawFeeBps: Number(pool.withdrawFee ?? 0),
      curator: configurator || undefined,
      curatorName,
      supplyRate: scaleRate(pool.supplyRate),
      baseInterestRate: scaleRate(pool.baseInterestRate),
      asset: assetMeta,
      priceUsd: priceUsd || undefined,
      totalAssetsFormatted,
      totalAssetsUsd: totalAssetsFormatted * priceUsd,
      liquidity: liquidityRaw,
      liquidityFormatted,
      liquidityUsd: liquidityFormatted * priceUsd,
    }

    // Multiple CMs may share a pool — MarketCompressor returns one
    // MarketData per pool, so we overwrite instead of appending. The
    // last one wins (they're all equivalent at this layer).
    out[underlying] = entry
  }

  return out
}
